import { apiRequest } from './client';
import type { MyTodo, NodeTodosPayload, ProjectsPayload, Todo, TodoDraft, TodoState } from '../lib/todos';

/**
 * To-dos (V0.50) — the work items that live on a map node — and, since V0.50.2, the PROJECT a
 * node can be marked as.
 *
 * THE TYPES LIVE IN `lib/todos.ts` AND ARE RE-EXPORTED HERE (V0.50.2), the `lib/docTable.ts` ↔
 * `api/docs.ts` arrangement: the e2e suite imports the pure library to recompute the Projects
 * tab's figures, and this module reaches Vite-typed config the suite's compiler does not know.
 * Callers in the app may import them from either place.
 *
 * UNLIKE EVERY OTHER `/api/me` LIST, THESE ROWS CARRY THEIR OWN DISPLAY. `api/me.ts`'s header
 * states the house rule: the server ships bare references and the client resolves labels from
 * the map state it already holds, which makes rows locale-correct and permission-filtered for
 * free. That rule is right for a favourite, which IS a node, and wrong here — a to-do's title
 * exists nowhere but its own row, so a bare reference would name nothing. The node context it
 * carries beside the title (`nodeLabel`, `nodeColor`) rides along for the same reason the
 * feed's rows carry theirs: the pane groups by node, and re-deriving the heading client-side
 * would break the moment a to-do outlived its node in somebody's stale snapshot.
 *
 * `dueDate` is a bare `YYYY-MM-DD` and deliberately not a Date: it is a DAY, and the moment it
 * becomes a Date somebody formats it in local time and a task due today reads as overdue for
 * everybody west of Greenwich.
 */

export type {
  MyTodo,
  NodeTodosPayload,
  ProjectDraft,
  ProjectRow,
  ProjectsPayload,
  Todo,
  TodoDraft,
  TodoState,
} from '../lib/todos';

/**
 * The node's list, and whether the node is a PROJECT (V0.50.2) — one fetch, because the popup's
 * To-dos section is where both are read. A server one release older answers without the
 * `project` key; callers read `r.project ?? null`.
 */
export const fetchNodeTodos = (nodeId: string): Promise<NodeTodosPayload> =>
  apiRequest(`/api/nodes/${encodeURIComponent(nodeId)}/todos`);

export const createTodo = (nodeId: string, draft: TodoDraft): Promise<Todo> =>
  apiRequest(`/api/nodes/${encodeURIComponent(nodeId)}/todos`, { method: 'POST', body: draft });

export const updateTodo = (id: string, draft: TodoDraft): Promise<Todo> =>
  apiRequest(`/api/todos/${encodeURIComponent(id)}`, { method: 'PUT', body: draft });

/**
 * Tick and un-tick — ITS OWN CALL, mirroring its own route.
 *
 * The separation is the feature, not a tidiness: the assignee may reach this without
 * `EDIT_TODOS`, and folding `state` into `updateTodo` would route that through a permission
 * they do not have. See `server/src/routes/todos.ts` rule 3.
 */
export const setTodoState = (id: string, state: TodoState): Promise<Todo> =>
  apiRequest(`/api/todos/${encodeURIComponent(id)}/state`, { method: 'PATCH', body: { state } });

export const deleteTodo = (id: string): Promise<void> =>
  apiRequest(`/api/todos/${encodeURIComponent(id)}`, { method: 'DELETE' });

/**
 * A WHOLE PERMUTATION of the node's to-dos — a partial list answers 409.
 *
 * `parents` (V0.51.1) carries the hierarchy alongside the order, every task mapped to its
 * parent or to null. Both halves ride ONE request because a task dragged under a parent lands
 * in a particular place among its new siblings: sending the move and the order separately would
 * leave a moment where the list is arranged by nobody. Omitted, the hierarchy is untouched and
 * this is the pure re-order it has always been.
 */
export const reorderTodos = (
  nodeId: string,
  ids: string[],
  parents?: Record<string, string | null>,
): Promise<{ todos: Todo[] }> =>
  apiRequest(`/api/nodes/${encodeURIComponent(nodeId)}/todos/order`, {
    method: 'PUT',
    body: parents ? { ids, parents } : { ids },
  });

/** Everything assigned to me, open by default. `?include=done` for the rest. */
export const fetchMyTodos = (locale: string, includeDone = false): Promise<{ todos: MyTodo[] }> =>
  apiRequest('/api/me/todos', { query: { locale, ...(includeDone ? { include: 'done' } : {}) } });

// Marking a node as a project is `app/mapWrites.ts`'s `putNodeProject` (V0.50.3), not a call
// here: the mark rides the map payload and the write adopts the `dataVersion` it returns, which
// is that module's job — the documentation plan's precedent.

/** Every project I may see, with the to-dos inside them — the Projects tab's one read. */
export const fetchProjects = (): Promise<ProjectsPayload> => apiRequest('/api/projects');
